import React, { useMemo } from 'react';
import { css, cx } from '@linaria/atomic';
import { Icon, type IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { PopoverMenu, PopoverMenuItem, usePopoverMenu } from '../../popover';
import { ActionDropdownChip } from '../atoms';

const alignmentActionIconStyle1 = css`
  margin-right: ${theme.spacing(0.5)};
`;

const alignmentActionPopoverMenuStyle2 = css`
  max-width: 160px;
`;

type AlignmentOptions = 'left' | 'center' | 'right' | 'justify';

type AlignmentActionProps = {
  value?: AlignmentOptions;
  onChange: (align: AlignmentOptions) => void;
} & Pick<React.ComponentProps<typeof ActionDropdownChip>, 'showBoxShadow' | 'compact' | 'className'>;

export const AlignmentAction = ({
  value = 'left',
  showBoxShadow = true,
  compact = true,
  onChange,
  ...rest
}: AlignmentActionProps) => {
  const { getTriggerProps, getMenuProps, getItemProps, isOpen } = usePopoverMenu<HTMLButtonElement | HTMLAnchorElement>(
    {
      interactionOptions: {
        listNavigation: {
          selectedIndex: selectedIndex(value),
        },
      },
      placement: 'bottom-start',
      middlewareOptions: {
        offset: {
          crossAxis: 0,
          mainAxis: 12,
        },
      },
    }
  );

  const activeAlignmentIcon = useMemo(() => alignmentItems.find((val) => val.value === value)?.icon, [value]);

  return (
    <>
      <ActionDropdownChip
        showBoxShadow={showBoxShadow}
        isOpen={isOpen}
        isActive={isOpen}
        compact={compact}
        {...getTriggerProps()}
        {...rest}
      >
        {activeAlignmentIcon && (
          <Icon name={activeAlignmentIcon} className={cx('alignment-icon', alignmentActionIconStyle1)} />
        )}
      </ActionDropdownChip>
      <PopoverMenu {...getMenuProps()} className={alignmentActionPopoverMenuStyle2} returnFocus={false}>
        {alignmentItems.map(({ label, value, icon }, index) => (
          <PopoverMenuItem
            Icon={() => <Icon name={icon} />}
            key={label}
            {...getItemProps({
              index,
              onClick: () => onChange(value),
            })}
          >
            {label}
          </PopoverMenuItem>
        ))}
      </PopoverMenu>
    </>
  );
};

const selectedIndex = (alignment: string) => {
  const index = alignmentItems.findIndex((alignObj) => alignObj.value === alignment);
  return index < 0 ? null : index;
};

const alignmentItems = [
  {
    icon: 'align-left-small',
    label: 'Align Left',
    value: 'left',
  },
  {
    icon: 'align-center-small',
    label: 'Align Center',
    value: 'center',
  },
  {
    icon: 'align-right-small',
    label: 'Align Right',
    value: 'right',
  },
  {
    icon: 'justify-small',
    label: 'Justify',
    value: 'justify',
  },
] as const satisfies { icon: IconName; label: string; value: string }[];
